import { useState } from 'react'
import { Button } from '~/components/common/Button'
import { ModalId, useModalController } from '~/hooks/useModal'
import { Diff } from '~/protos/agents'

interface ImageViewerModalProps {
  title: string
  imageDiffs: Diff[]
  modalId?: ModalId
}

export function ImageViewerModal({ title, imageDiffs, modalId }: ImageViewerModalProps) {
  const { closeModal } = useModalController(modalId || ModalId.ImageViewer)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  if (!imageDiffs || imageDiffs.length === 0) {
    return (
      <div className='flex flex-col h-full'>
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-default-500'>No diagrams available</p>
        </div>
        <div className='border-t border-default-200 p-4 flex justify-end'>
          <Button variant='outlined' onClick={closeModal}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  const currentImage = imageDiffs[selectedImageIndex]
  const imageContent = currentImage?.bFileContent || currentImage?.aFileContent
  const fileName = currentImage?.bFilePath || currentImage?.aFilePath || 'diagram.png'
  const displayName = fileName.replace('.b64', '').split('/').pop() || 'diagram.png'

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='border-b border-default-200 p-4'>
        <h2 className='text-lg font-medium text-default-900'>{title}</h2>
        {imageDiffs.length > 1 && (
          <div className='mt-3 flex flex-wrap gap-2'>
            {imageDiffs.map((_, index) => {
              const imgFileName = (
                imageDiffs[index]?.bFilePath ||
                imageDiffs[index]?.aFilePath ||
                `diagram-${index + 1}.png`
              )
                .replace('.b64', '')
                .split('/')
                .pop()
              return (
                <Button
                  key={index}
                  variant={selectedImageIndex === index ? 'primary' : 'outlined'}
                  size='sm'
                  onClick={() => setSelectedImageIndex(index)}
                >
                  {imgFileName}
                </Button>
              )
            })}
          </div>
        )}
      </div>

      {/* Image Content */}
      <div className='flex-1 overflow-auto p-4'>
        {imageContent ? (
          <div className='flex flex-col items-center'>
            <img
              src={imageContent}
              alt={displayName}
              className='max-w-full h-auto border border-default-200 rounded-lg shadow-sm'
              style={{ maxHeight: 'calc(100vh - 200px)' }}
            />
            <p className='mt-2 text-sm text-default-500'>{displayName}</p>
          </div>
        ) : (
          <div className='flex items-center justify-center h-full'>
            <p className='text-default-500'>Unable to load image</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className='border-t border-default-200 p-4 flex justify-between'>
        <div className='flex gap-2'>
          {imageContent && (
            <Button
              variant='outlined'
              onClick={() => {
                const link = document.createElement('a')
                link.href = imageContent
                link.download = displayName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }}
            >
              Download
            </Button>
          )}
        </div>
        <Button variant='outlined' onClick={closeModal}>
          Close
        </Button>
      </div>
    </div>
  )
}
